import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PenSquare, Sparkles, Send, Upload, Loader2, Eye, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { adminHeaders } from '@/lib/adminAuth';

const CATEGORIES = ['Federation', 'HumanCodex', 'Technology', 'Philosophy', 'Architecture', 'Research', 'Guide', 'Consciousness', 'General'];

export default function BlogMaker({ apiUrl, embed = false }) {
  const [mode, setMode] = useState('write'); // write | ai
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('General');
  const [tags, setTags] = useState('');
  const [author, setAuthor] = useState('Roger Keyserling');
  const [aiTopic, setAiTopic] = useState('');
  const [aiCount, setAiCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [published, setPublished] = useState(null);
  const [embedCode, setEmbedCode] = useState(false);

  const handlePublish = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${apiUrl}/blog/posts`, {
        title, content, excerpt: excerpt || content.slice(0, 200) + '...',
        author, category, tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        published: true, featured: false, sources: [],
      }, { headers: adminHeaders() });
      setPublished(res.data);
      toast.success('Blog post published!');
    } catch (e) {
      toast.error('Failed to publish: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiTopic.trim() && aiCount < 1) {
      toast.error('Enter a topic or set count');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${apiUrl}/blog/generate?count=${aiCount}`, {}, { headers: adminHeaders() });
      toast.success(res.data.message || 'AI generation started');
      setPublished({ ai: true, message: res.data.message });
    } catch (e) {
      toast.error('AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle(''); setContent(''); setExcerpt(''); setTags('');
    setPublished(null); setPreview(false);
  };

  const handleSaveToLibrary = async () => {
    if (!title.trim() || !content.trim()) { toast.error('Title and content required'); return; }
    setLoading(true);
    try {
      await axios.post(`${apiUrl}/blog/save-to-library`, { title, content, author, category, tags: tags.split(',').map(t => t.trim()).filter(Boolean) }, { headers: adminHeaders() });
      toast.success('Saved to Library as markdown');
    } catch (e) { toast.error('Save failed'); }
    finally { setLoading(false); }
  };

  const handlePublishToFederation = async () => {
    if (!title.trim() || !content.trim()) { toast.error('Title and content required'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${apiUrl}/blog/publish-to-federation`, { title, content, author, category, tags: tags.split(',').map(t => t.trim()).filter(Boolean) }, { headers: adminHeaders() });
      toast.success(`Published to ${res.data.delivered || 0} Federation nodes`);
    } catch (e) { toast.error('Federation publish failed'); }
    finally { setLoading(false); }
  };

  const containerClass = embed
    ? 'bg-slate-950 text-slate-200 p-4 min-h-screen'
    : 'bg-slate-950 text-slate-200 px-4 py-8 max-w-4xl mx-auto min-h-screen';

  return (
    <div className={containerClass} data-testid="blog-maker">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <PenSquare className="w-5 h-5 text-cyan-400" />
            Blog Maker
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Write manually or let AI draft for you</p>
        </div>
        {embedCode && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEmbedCode(false)}>
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
              <h3 className="text-sm font-bold text-white mb-3">Embed Code</h3>
              <textarea
                readOnly
                className="w-full bg-slate-950 border border-slate-700 rounded p-3 text-xs text-cyan-400 font-mono h-24 resize-none"
                value={`<iframe src="${window.location.origin}/embed/blog-maker" width="100%" height="800" frameborder="0" style="border:none;border-radius:12px;"></iframe>`}
              />
              <Button size="sm" className="mt-3" onClick={() => { navigator.clipboard.writeText(`<iframe src="${window.location.origin}/embed/blog-maker" width="100%" height="800" frameborder="0" style="border:none;border-radius:12px;"></iframe>`); toast.success('Copied!'); }}>
                Copy to Clipboard
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Mode Toggle + Embed Button */}
      <div className="flex items-center gap-2 mb-6">
        <Button size="sm" variant={mode === 'write' ? 'default' : 'outline'} onClick={() => setMode('write')}
          className={mode === 'write' ? 'bg-cyan-700 text-white' : 'border-slate-700 text-slate-400'} data-testid="mode-write">
          <PenSquare className="w-3.5 h-3.5 mr-1.5" /> Write
        </Button>
        <Button size="sm" variant={mode === 'ai' ? 'default' : 'outline'} onClick={() => setMode('ai')}
          className={mode === 'ai' ? 'bg-violet-700 text-white' : 'border-slate-700 text-slate-400'} data-testid="mode-ai">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Generate
        </Button>
        {!embed && (
          <Button size="sm" variant="outline" onClick={() => setEmbedCode(true)} className="ml-auto border-slate-700 text-slate-500" data-testid="embed-btn">
            <Code className="w-3.5 h-3.5 mr-1.5" /> Embed
          </Button>
        )}
      </div>

      {/* ===== SUCCESS STATE ===== */}
      {published && (
        <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-6 mb-6 text-center" data-testid="publish-success">
          <div className="text-emerald-400 text-lg font-bold mb-2">
            {published.ai ? 'AI Generation Started' : 'Published!'}
          </div>
          <p className="text-sm text-slate-400 mb-4">
            {published.ai ? published.message : `"${published.title}" is now live on the blog.`}
          </p>
          <div className="flex gap-3 justify-center">
            <Button size="sm" onClick={resetForm} className="bg-slate-800 hover:bg-slate-700">Write Another</Button>
            {published.slug && (
              <a href={`/blog/${published.slug}`} className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-cyan-700 hover:bg-cyan-600 text-white rounded-lg">
                <Eye className="w-3.5 h-3.5" /> View Post
              </a>
            )}
          </div>
        </div>
      )}

      {/* ===== MANUAL WRITE MODE ===== */}
      {mode === 'write' && !published && (
        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Title</label>
            <Input
              value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Your blog post title..."
              className="bg-slate-900/80 border-slate-700/60 text-white h-11"
              data-testid="blog-title"
            />
          </div>

          {/* Meta row */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Author</label>
              <Input value={author} onChange={e => setAuthor(e.target.value)}
                className="bg-slate-900/80 border-slate-700/60 text-sm" data-testid="blog-author" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full h-9 bg-slate-900/80 border border-slate-700/60 rounded-md px-3 text-sm text-white" data-testid="blog-category">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Tags (comma-separated)</label>
              <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="federation, ai, philosophy"
                className="bg-slate-900/80 border-slate-700/60 text-sm" data-testid="blog-tags" />
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Excerpt (optional — auto-generated if blank)</label>
            <Input value={excerpt} onChange={e => setExcerpt(e.target.value)}
              placeholder="Brief summary..."
              className="bg-slate-900/80 border-slate-700/60 text-sm" data-testid="blog-excerpt" />
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Content</label>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-600">{content.split(/\s+/).filter(Boolean).length} words</span>
                <Button size="sm" variant="ghost" onClick={() => setPreview(!preview)}
                  className="h-6 text-[10px] text-slate-500 hover:text-white px-2">
                  {preview ? 'Edit' : 'Preview'}
                </Button>
              </div>
            </div>
            {preview ? (
              <div className="min-h-[300px] bg-slate-900/60 border border-slate-700/40 rounded-lg p-4 prose prose-invert prose-sm max-w-none">
                <h2 className="text-lg font-bold text-white mb-2">{title || 'Untitled'}</h2>
                <div className="text-xs text-slate-500 mb-3">{author} · {category}</div>
                {content.split('\n\n').map((p, i) => (
                  <p key={i} className="text-sm text-slate-300 leading-relaxed mb-3">{p}</p>
                ))}
              </div>
            ) : (
              <textarea
                value={content} onChange={e => setContent(e.target.value)}
                placeholder="Write your blog post here... Use plain text or Markdown."
                className="w-full min-h-[300px] bg-slate-900/80 border border-slate-700/60 rounded-lg p-4 text-sm text-slate-200 placeholder-slate-600 resize-y focus:outline-none focus:border-cyan-500/60 leading-relaxed"
                data-testid="blog-content"
              />
            )}
          </div>

          {/* Publish Buttons */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button onClick={handlePublish} disabled={loading || !title.trim() || !content.trim()}
              className="bg-cyan-700 hover:bg-cyan-600 text-white gap-2" data-testid="publish-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Publish to Blog
            </Button>
            <Button onClick={handleSaveToLibrary} disabled={loading || !title.trim() || !content.trim()}
              variant="outline" className="gap-2 border-slate-700 text-slate-300" data-testid="save-library-btn">
              <Upload className="w-4 h-4" /> Save to Library
            </Button>
            <Button onClick={handlePublishToFederation} disabled={loading || !title.trim() || !content.trim()}
              variant="outline" className="gap-2 border-emerald-700/50 text-emerald-400" data-testid="publish-federation-btn">
              <Send className="w-4 h-4" /> Publish to Federation
            </Button>
          </div>
        </div>
      )}

      {/* ===== AI GENERATE MODE ===== */}
      {mode === 'ai' && !published && (
        <div className="space-y-4">
          <div className="bg-violet-900/10 border border-violet-700/20 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-violet-400" />
              <h2 className="text-sm font-bold text-white">AI Blog Generator</h2>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              GPT-5.2 writes Federation-themed blog posts from research. Each post takes ~60 seconds to generate.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Number of Posts</label>
                <select value={aiCount} onChange={e => setAiCount(Number(e.target.value))}
                  className="w-full h-9 bg-slate-900/80 border border-slate-700/60 rounded-md px-3 text-sm text-white" data-testid="ai-count">
                  {[1, 2, 3, 5].map(n => <option key={n} value={n}>{n} post{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 block">Topic (optional)</label>
                <Input value={aiTopic} onChange={e => setAiTopic(e.target.value)} placeholder="AI consciousness, federation..."
                  className="bg-slate-900/80 border-slate-700/60 text-sm" data-testid="ai-topic" />
              </div>
            </div>
            <Button onClick={handleAiGenerate} disabled={loading}
              className="bg-violet-700 hover:bg-violet-600 text-white gap-2" data-testid="ai-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? 'Generating...' : 'Generate with AI'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
